import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import api from "@/lib/axios";
import type { RootState } from "@/store/store";

export interface ProductSearchResult {
  id: string | number;
  productName: string;
  tradeName: string;
  companyCode: string;
  manufacturerHolder: string;
  authorizationCountry: string;
  authorizationNumber: string;
  comments: string;
  formulation?: string;
  ingredients?: string;
  product_description?: string;
  license_award_date?: string;
  product_id?: string;
  product_type?: string;
  unit?: string;
  generic_name?: string;
  strength?: string;
  product_ibd?: string;
  license_type?: string;
}

interface ProductsState {
  items: ProductSearchResult[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error?: string;
  loaded: boolean;
}

const initialState: ProductsState = {
  items: [],
  status: "idle",
  loaded: false,
};

export const fetchAllProducts = createAsyncThunk<
  ProductSearchResult[],
  void,
  { state: RootState }
>(
  "products/fetchAll",
  async () => {
    const queryParams = new URLSearchParams();
    queryParams.append("page", "1");
    queryParams.append("page_size", "1000");

    const url = `/search_product?${queryParams.toString()}`;
    const response = await api.get(url);

    const transformedData: ProductSearchResult[] = [];
    let rowId = 1;

    if (response.data?.data?.items) {
      response.data.data.items.forEach((product: any) => {
        if (product.licenses && product.licenses.length > 0) {
          product.licenses.forEach((license: any) => {
            transformedData.push({
              id: `${product.id}-${license.id}-${rowId++}`,
              productName: product.product_name || "",
              tradeName: license.trade_name || "",
              companyCode: product.product_id || "",
              manufacturerHolder: license.market_authorization_holder || "",
              authorizationCountry: license.authorization_country || "",
              authorizationNumber: license.authorization_number || "",
              comments: license.comments || product.product_description || "",
              formulation: product.formulation || "",
              ingredients: product.ingredients || "",
              product_description: product.product_description || "",
              license_award_date: license.license_award_date || "",
              product_id: product.product_id || "",
              product_type: product.product_type || "",
              unit: product.unit || "",
              generic_name: product.generic_name || "",
              strength: product.strength || "",
              product_ibd: product.product_ibd || "",
              license_type: license.license_type || "",
            });
          });
        } else {
          transformedData.push({
            id: `${product.id}-no-license-${rowId++}`,
            productName: product.product_name || "",
            tradeName: "",
            companyCode: product.product_id || "",
            manufacturerHolder: "",
            authorizationCountry: "",
            authorizationNumber: "",
            comments: product.product_description || "",
            formulation: product.formulation || "",
            ingredients: product.ingredients || "",
            product_description: product.product_description || "",
            license_award_date: "",
            product_id: product.product_id || "",
            product_type: product.product_type || "",
            unit: product.unit || "",
            generic_name: product.generic_name || "",
            strength: product.strength || "",
            product_ibd: product.product_ibd || "",
            license_type: "",
          });
        }
      });
    }

    return transformedData;
  },
  {
    // Prevent duplicate network requests if already loaded or currently loading
    condition: (_, { getState }) => {
      const state = getState();
      const status = state.products?.status;
      const loaded = state.products?.loaded;
      if (status === "loading" || loaded) {
        return false;
      }
      return true;
    },
  }
);

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchAllProducts.fulfilled,
        (state, action: PayloadAction<ProductSearchResult[]>) => {
          state.status = "succeeded";
          // Cache full list
          state.items = action.payload;
          state.loaded = true;
        }
      )
      .addCase(fetchAllProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message || "Failed to load products";
      });
  },
});

export default productsSlice.reducer;

export const selectAllProducts = (state: RootState) => state.products.items;
export const selectProductsLoaded = (state: RootState) => state.products.loaded;
export const selectProductsStatus = (state: RootState) => state.products.status;
export const selectProductsError = (state: RootState) => state.products.error;
